import * as THREE from 'three'
import { RunConfig } from '../../config/runs'
import { getElevation } from '../../world/terrain/SlopeTerrain'

/**
 * Creates boulders scattered across the slope — mostly on the edges
 * and off-piste areas, some on the run itself on harder difficulties.
 */

function seededRandom(seed: number): number {
  const n = Math.sin(seed * 237.3) * 43758.5453
  return n - Math.floor(n)
}

function createBoulder(size: number): THREE.Mesh {
  // Irregular boulder shape — stretched and rotated icosahedron
  const geo = new THREE.IcosahedronGeometry(size, 1)

  // Distort vertices for a natural rocky look
  const positions = geo.attributes.position
  for (let i = 0; i < positions.count; i++) {
    const x = positions.getX(i)
    const y = positions.getY(i)
    const z = positions.getZ(i)
    const noise = 0.7 + Math.sin(x * 5 + y * 3) * 0.3
    positions.setX(i, x * noise)
    positions.setY(i, y * (0.6 + Math.abs(Math.sin(z * 4)) * 0.4))
    positions.setZ(i, z * noise)
  }
  geo.computeVertexNormals()

  const mat = new THREE.MeshStandardMaterial({
    color: 0x6b6b6b,
    roughness: 0.9,
    metalness: 0.1,
  })

  const mesh = new THREE.Mesh(geo, mat)
  mesh.castShadow = true
  mesh.receiveShadow = true
  return mesh
}

export interface BoulderData {
  mesh: THREE.Mesh
  position: THREE.Vector3
  radius: number
}

export function createBoulders(config: RunConfig): BoulderData[] {
  const boulders: BoulderData[] = []

  // Boulders on the sides of the run (always)
  const sideBoulderCount = Math.floor(config.length / 25)
  for (let i = 0; i < sideBoulderCount; i++) {
    const seed = i * 31.7 + 200
    const side = i % 2 === 0 ? -1 : 1
    // Place outside the run but within the terrain
    const x = side * (config.width / 2 + 3 + seededRandom(seed) * config.width * 0.5)
    const z = -config.length / 2 + 20 + (i / sideBoulderCount) * config.length * 0.9
    const zOffset = (seededRandom(seed + 1) - 0.5) * 15
    const size = 0.5 + seededRandom(seed + 2) * 1.5

    const groundY = getElevation(x, z + zOffset)
    const boulder = createBoulder(size)
    boulder.position.set(x, groundY + size * 0.3, z + zOffset)
    boulder.rotation.set(
      seededRandom(seed + 3) * Math.PI,
      seededRandom(seed + 4) * Math.PI,
      seededRandom(seed + 5) * 0.5
    )

    boulders.push({
      mesh: boulder,
      position: new THREE.Vector3(x, groundY, z + zOffset),
      radius: size * 0.8,
    })
  }

  // A few boulders ON the run for harder difficulties
  const onRunDensity: Record<string, number> = {
    green: 0,
    blue: 3,
    black: 8,
    doubleBlack: 12,
  }
  const onRunCount = onRunDensity[config.difficulty] || 0
  for (let i = 0; i < onRunCount; i++) {
    const seed = i * 47.1 + 500
    const x = (seededRandom(seed) - 0.5) * (config.width * 0.6)
    const z = -config.length / 2 + 80 + (i / Math.max(onRunCount, 1)) * config.length * 0.7
    const size = 0.4 + seededRandom(seed + 1) * 0.8

    const groundY = getElevation(x, z)
    const boulder = createBoulder(size)
    boulder.position.set(x, groundY + size * 0.3, z)
    boulder.rotation.set(
      seededRandom(seed + 2) * Math.PI,
      seededRandom(seed + 3) * Math.PI,
      0
    )

    boulders.push({
      mesh: boulder,
      position: new THREE.Vector3(x, groundY, z),
      radius: size * 0.8,
    })
  }

  return boulders
}
